import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { extractApiError } from '../../api';

/**
 * Smart retry: don't retry 4xx (client errors — won't change on retry), do
 * retry 5xx and network errors up to 2x. Replaces the unconditional `retry: 2`
 * which made bad-input bugs invisible behind retry storms.
 */
function smartRetry(failureCount: number, err: unknown) {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds
      retry: smartRetry,
      refetchOnWindowFocus: true,
    },
  },
  /**
   * Global query failure handler — captures every uncaught query error in
   * Sentry AND surfaces a toast. Previously failures only went to Sentry,
   * which meant the user saw a stuck spinner or empty state with no
   * indication that the API call had failed (and no retry hint). Mirrors
   * the mutationCache pattern below for consistency.
   *
   * Pages that want to suppress the toast (e.g. the cold-start "no data"
   * cases that intentionally render their own copy) can opt out by passing
   * `meta: { suppressToast: true }` on the useQuery call.
   *
   * 401s are excluded — the axios response interceptor already handles
   * those by triggering a refresh + redirect to /login if it fails.
   */
  queryCache: new QueryCache({
    onError: (err, query) => {
      Sentry.captureException(err, { tags: { type: 'query' } });
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) return;  // axios interceptor handles re-auth
      const suppress = (query.meta as { suppressToast?: boolean } | undefined)?.suppressToast;
      if (!suppress) {
        toast.error(extractApiError(err));
      }
    },
  }),
  /**
   * Global mutation failure handler — captures Sentry AND shows a toast.
   * Only fires for mutations whose own onError doesn't suppress propagation;
   * page-level handlers can opt out by setting `meta: { suppressToast: true }`
   * on the useMutation call.
   */
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      Sentry.captureException(err, { tags: { type: 'mutation' } });
      const suppress = (mutation.meta as { suppressToast?: boolean } | undefined)?.suppressToast;
      if (!suppress) {
        toast.error(extractApiError(err));
      }
    },
  }),
});
